import React, {
  Dispatch, useContext, useEffect, useRef, useState,
} from 'react';
import { Box, IconButton, Modal as MaterialModal, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { LOGOS } from 'shared/constants';
import CreateProcessContext from 'shared/contexts/CreateProcessContext';
import UserContext from 'shared/contexts/UserContext';
import S3Avatar from 'components/S3Avatar';
import StepTracker from 'components/StepTracker';
import {
  Company,
  CreateProcessRequestInput,
  FileData,
  JobDescription,
  ProcessRequestState,
} from 'API';
import { useImmer } from 'use-immer';
import { nanoid } from 'nanoid';
import useGetForeignNational from 'shared/hooks/useGetForeignNational';
import StartPanel from './ProcessModalPanels/Start';
import DependentsPanel from './ProcessModalPanels/Dependents';
import SelectPanel from './ProcessModalPanels/Select';
import DetailsPanel from './ProcessModalPanels/Details';
import CompletePanel from './ProcessModalPanels/Complete';

const maxWidth = '35rem';

export const getCreateProcessStyles = (theme: Theme) => ({
  avatar: {
    marginRight: '1rem',
    width: '4rem',
    height: '4rem',
  },
  buttonMain: {
    alignSelf: 'center',
    display: 'flex',
  },
  buttonNav: {
    background: theme.palette.backgroundPrimary,
    paddingBottom: '1rem',
    bottom: 0,
    display: 'flex',
    justifyContent: 'space-between',
    maxWidth,
    paddingTop: '0.5rem',
    position: 'fixed',
    width: '100%',
  },
  employeeTab: {
    display: 'flex',
    flexDirection: 'column',
    margin: '1rem 0',
  },
  formControl: {
    marginBottom: '2rem',
    width: '100%',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  header: {
    alignItems: 'flex-start',
    display: 'flex',
    gridRow: '1 / 2',
    maxWidth,
  },
  h1: {
    color: theme.palette.primary2,
    flexGrow: 1,
    fontSize: '2rem',
    fontWeight: theme.typography.fontWeightRegular as any,
    marginRight: '1rem',
    marginTop: '0.25rem',
  },
  memo: {
    margin: 0,
    fontSize: '1.5rem',
    fontWeight: theme.typography.fontWeightRegular as any,
  },
  memoContainer: {
    display: 'flex',
    gridRow: '3 / 4',
    maxWidth,
  },
  panel: {
    gridColumn: '1 / 2',
    gridRow: '1 / 2',
    maxWidth: `calc(${maxWidth} + 1rem)`,
    padding: '1rem 0.5rem 2.5rem',
    position: 'relative',
    '& > .MuiPaper-root': {
      marginBottom: '1rem',
    },
  },
  panelContainer: {
    display: 'grid',
    gridRow: '4 / 5',
    maxHeight: '100%',
    maxWidth: `calc(${maxWidth} + 1rem)`,
    width: '100%',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  root: {
    alignItems: 'start',
    background: `url('${LOGOS.grey}')
        no-repeat calc(100% + 5vw) calc(100% + 5vw),
        ${theme.palette.backgroundPrimary}`,
    backgroundSize: '30vw 30vw',
    display: 'grid',
    gridTemplateColumns: '100%',
    gridTemplateRows: 'auto auto auto 1fr',
    outline: 0,
    height: '100vh',
    justifyItems: 'center',
    padding: '0 1rem',
    width: '100vw',
    overflow: 'auto',
  },
  stepper: {
    gridRow: '2 / 3',
    maxWidth,
    padding: 0,
  },

  modalHeader: {
    position: 'sticky',
    top: '0',
    gap: '1rem',
    display: 'flex',
    width: '100%',
    background:
      'url(/img/logos/mh-logo--circle-grey.svg) no-repeat calc(100% + 5vw) calc(100% + 5vw), #fdfbfa',
    zIndex: 2,
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: '1rem',
    paddingBottom: 20,
    paddingTop: '1rem',
  },
});

const nationalTempID = nanoid();

interface Props {
  open: boolean;
  setOpen: Dispatch<boolean>;
}

const CreateProcessModal: React.FC<Props> = ({
  open,
  setOpen,
}: Props) => {
  const [userState] = useContext(UserContext);
  const { person } = userState;

  const initialProcessRequestInput: CreateProcessRequestInput = {
    employeeData: {
      firstname: '',
      lastname: '',
      tempID: nationalTempID,
    },
    employmentData: {},
    requestDate: new Date().toISOString(),
    requesterID: person.id,
    slug: '-', // Temp slug until we know the FN’s name and processs
    state: ProcessRequestState.REQUESTED,
  };

  const theme = useTheme();
  const classes = getCreateProcessStyles(theme);
  const hasAvatar = Boolean(person.avatar?.original?.key);
  const [process, setProcess] = useImmer<CreateProcessRequestInput>(
    initialProcessRequestInput,
  );
  const [resume, setResume] = useState<FileData>();
  const [uploadedDescription, setUploadedDescription] = useState<FileData>();
  const selectedNational = useGetForeignNational(process.foreignNationalID);
  const [selectedJob, setSelectedJob] = useState<JobDescription>();
  const [selectedCompany, setSelectedCompany] = useState<Company>();
  const [isExistingJob, setIsExistingJob] = useState<boolean | undefined>(undefined);
  const [emailIsTaken, setEmailIsTaken] = useState(false);
  const [titleIsTaken, setTitleIsTaken] = useState(false);
  const panelContainerRef = useRef<HTMLDivElement>(null);

  const panels: ProcessPanels = {
    start: {
      label: 'Start Request',
      memo: `${person.firstname ? `Hi, ${person.firstname}. ` : ''} 
      Which employee is this a request for?`,
    },
    startDependents: {
      label: 'Add Dependents',
      memo: `Does this employee need work authorization support for 
             dependents?`,
    },
    select: {
      label: 'Select Process',
      memo: 'Which process would you like to sponsor for this employee?',
    },
    details: {
      label: 'Add Details',
      memo: 'What are the details of this employee’s job position?',
    },
    complete: {
      label: 'Complete Request',
      memo: 'Thank you. Your request has been submitted and is now in review by your legal team.',
    },
  };

  // Steps are slightly different than panels in that the “start” step
  // may have two panels, if the user is manually entering a national.
  const panelNames = Object.keys(panels) as Array<ProcessPanelName>;
  const disableSteps = panelNames.filter((name) => name === 'startDependents');
  const [activePanel, setActivePanel] = useState<ProcessPanelName>(panelNames[0]);

  const resetState = () => {
    setActivePanel(panelNames[0]);
    setProcess(initialProcessRequestInput);
    setIsExistingJob(undefined);
    setResume(undefined);
  };

  const handleClose = () => {
    resetState();
    setOpen(false);
  };

  const currentIndex = panelNames.indexOf(activePanel);

  const goToPanel = (panel: ProcessPanelName) => {
    panelContainerRef?.current?.scrollTo(0, 0);
    setActivePanel(panel);
  };

  /**
   * Let the user step back, but not forward, and not if
   * they are on the "complete" step.
   */
  const canGoBack = (index: number): boolean => index < currentIndex
    && panelNames[currentIndex] !== 'complete';

  useEffect(() => {
    setProcess((draft) => {
      draft.requesterID = person.id;
    });
  }, []);

  return (
    <CreateProcessContext.Provider
      value={{
        activePanel,
        goToPanel,
        isExistingJob,
        panelNames,
        process,
        resume,
        selectedCompany,
        selectedJob,
        selectedNational,
        setActivePanel,
        setIsExistingJob,
        setProcess,
        setResume,
        setSelectedCompany,
        setSelectedJob,
        setTitleIsTaken,
        titleIsTaken,
        setEmailIsTaken,
        emailIsTaken,
        setUploadedDescription,
        uploadedDescription,
      }}
    >
      <MaterialModal
        open={open}
        onClose={handleClose}
        aria-labelledby="process-modal-header"
      >
        <Box sx={classes.root}>
          <Box sx={classes.modalHeader}>
            <Box component="header" sx={classes.header}>
              <Typography
                variant="h1"
                id="process-modal-header"
                sx={classes.h1}
              >
                Start a New Process Request
              </Typography>
              <IconButton onClick={handleClose} data-title="close-button" size="large">
                <CloseIcon color="primary" />
              </IconButton>
            </Box>

            <Box sx={classes.stepper}>
              <StepTracker
                activeStep={activePanel === 'startDependents' ? 'start' : activePanel}
                steps={panels}
                disabledSteps={disableSteps}
                onStepClick={(index: number) => {
                  if (canGoBack(index)) {
                    goToPanel(panelNames[index]);
                  }
                }}
              />
            </Box>

            <Box sx={classes.memoContainer} component="section">
              {hasAvatar && (
                <S3Avatar
                  className="avatar--shadow"
                  style={{ marginRight: '1rem', width: '4rem', height: '4rem' }}
                  picture={person.avatar}
                />
              )}
              <Box component="h1" sx={classes.memo}>{panels[activePanel].memo}</Box>
            </Box>

          </Box>

          <Box sx={classes.panelContainer} {...{ ref: panelContainerRef } as any}>
            {activePanel === 'start' && <StartPanel />}
            {activePanel === 'startDependents' && <DependentsPanel />}
            {activePanel === 'select' && <SelectPanel />}
            {activePanel === 'details' && <DetailsPanel />}
            {activePanel === 'complete'
              && <CompletePanel resetState={resetState} setOpen={setOpen} />}
          </Box>
        </Box>
      </MaterialModal>
    </CreateProcessContext.Provider>
  );
};

export default CreateProcessModal;
